import React from 'react';
import PropTypes from 'prop-types';
import View from "react-native-web/dist/exports/View";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import Platform from "react-native-web/dist/exports/Platform";
import { useThemeTokens } from '../ThemeProvider';
import Spacer from '../Spacer';
import { a11yProps, getTokensPropType, selectSystemProps, spacingProps, variantProp, viewProps } from '../utils';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps]);

/**
 * @typedef {import('../utils/props/spacingProps.js').SpacingIndex} SpacingIndex
 * @typedef {import('../utils/props/spacingProps.js').SpacingObject} SpacingObject
 */

/**
 * A basic divider component, horizontal by default. Color and thickness can be controlled by theme.
 * Divider is based on the flexbox model and uses `alignSelf: flex` to span its flex parent.
 *
 * ## Vertical
 *
 * Vertical dividers can be created by passing a `vertical` boolean prop.
 * Note that for a vertical divider the parent flex element **must** have flex direction set to row.
 * This is the default for web but not for React Native.
 *
 * In a flexbox row, vertical dividers will automatically size to their parent height.
 *
 * ## Space
 *
 * Use this to create **space either side of the divider**. For simple cases, pass a number referring to
 * a position on the theme's spacing scale; for example, this will put a Spacer of the smallest supported
 * size either side of the divider:
 *
 * ```jsx
 * <Divider space={1} />
 * ```
 *
 * `space` prop uses `useSpacingScale` and may accept a {@link SpacingObject} or a {@link SpacingIndex} number.
 *
 * To **reduce the length of a divider** as well as creating space between it and its neighbours, wrap it in
 * a `Box` component. For example, this will have the second-smallest theme-supported spacing value between
 * the dividing line and its neighbours, and will shorten the line at either end by the same amount:
 *
 * ```jsx
 * <Box space={2}><Divider /></Box>
 * ```
 *
 * ## Accessibility
 *
 * For accessibility purposes a divider component will be described with ARIA attributes, i.e. `role="separator"` and `aria-orientation="vertical/horizontal"`.
 */
const Divider = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    variant,
    vertical = false,
    space,
    tokens,
    ...rest
  } = _ref;
  const {
    color,
    width
  } = useThemeTokens('Divider', tokens, variant);
  const borderStyles = vertical ? {
    borderRightColor: color,
    borderRightWidth: width
  } : {
    borderBottomColor: color,
    borderBottomWidth: width
  };
  const a11y = Platform.OS === 'web' ?
  // There are aria attributes for dividers on web
  {
    // Early versions of React Native Web support 'aria-orientation' while
    // later versions only support accessibilityOrientation and map it. Supply both.
    'aria-orientation': vertical ? 'vertical' : 'horizontal',
    accessibilityOrientation: vertical ? 'vertical' : 'horizontal',
    accessibilityRole: 'separator'
  } :
  // There are no such equivalent attributes for native
  {};
  const {
    testID,
    ...selectedProps
  } = selectProps({
    ...a11y,
    ...rest
  });
  const divider = /*#__PURE__*/_jsx(View, {
    ref: ref,
    style: [styles.divider, borderStyles],
    testID: testID,
    ...selectedProps
  });
  if (!space) return divider;
  const spacerProps = {
    space,
    direction: vertical ? 'row' : 'column'
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Spacer, {
      ...spacerProps,
      testID: testID ? `${testID}-Spacer-before` : undefined
    }), divider, /*#__PURE__*/_jsx(Spacer, {
      ...spacerProps,
      testID: testID ? `${testID}-Spacer-after` : undefined
    })]
  });
});
Divider.displayName = 'Divider';
Divider.propTypes = {
  ...selectedSystemPropTypes,
  tokens: getTokensPropType('Divider'),
  /**
   * A number referring to a position on the space scale to create space on either side of the divider
   */
  space: spacingProps.types.spacingValue,
  variant: variantProp.propType,
  /**
   * By default, the divider is a horizontal line the full width of its parent.
   * With `vertical` prop and when inside a container with { flexDirection: row }, draws a full height vertical line.
   */
  vertical: PropTypes.bool
};
const styles = StyleSheet.create({
  divider: {
    alignSelf: 'stretch'
  }
});
export default Divider;